// kast-dev family file: queryClient; master: waypoint_wars/ui/common/api/queryClient.ts
// Copied unchanged into every kast-dev app that uses TanStack Query. Change
// the master, copy it everywhere, and run infra/scripts/check-family-files.sh.

import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

export interface QueryClientOptions {
  /** The HTTP status an error from the app's API client carries, if any. */
  statusOf: (error: unknown) => number | undefined;
  /**
   * The app's own 401 signal, for calls made outside Query (raw API calls,
   * sync engines). Returns an unsubscribe function.
   */
  subscribeToAuthErrors?: (listener: () => void) => () => void;
}

/**
 * Builds the app's QueryClient with the defaults every kast-dev app shares.
 *
 * A factory rather than a singleton so tests can build an isolated client
 * per test: a shared one would let one test's cached data decide whether the
 * next one passes.
 */
export function createQueryClient({ statusOf, subscribeToAuthErrors }: QueryClientOptions): QueryClient {
  // A 401 means the cached data belongs to a session that no longer exists.
  // Clear it so the next render refetches as whoever is signed in now.
  const clearOnAuthError = (error: unknown) => {
    if (statusOf(error) === 401) client.clear();
  };

  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError: clearOnAuthError }),
    mutationCache: new MutationCache({ onError: clearOnAuthError }),
    defaultOptions: {
      queries: {
        // Data here changes on human timescales. Thirty seconds makes moving
        // between screens instant without showing anything noticeably old.
        staleTime: 30_000,
        // Keep unused data five minutes so going back is instant.
        gcTime: 5 * 60_000,
        // These apps are left open; refetching on every focus is a request
        // storm for slow-moving data. Mounting a stale query already refetches.
        refetchOnWindowFocus: false,
        // One retry, and only for a server or network failure: a 4xx will not
        // improve, and retrying it just delays the error the user needs.
        retry: (failureCount, error) => {
          const status = statusOf(error);
          return failureCount < 1 && (status === undefined || status >= 500);
        },
      },
    },
  });

  subscribeToAuthErrors?.(() => client.clear());

  return client;
}

/**
 * Clears the cache whenever the signed-in user changes: sign-out, a different
 * account signing in, or a rejected refresh. Call once, under the provider,
 * with the session's stable user id (the Cognito `sub`), or null when signed
 * out. Without it, one person's cached data is shown to the next on any path
 * that doesn't reload the page.
 */
export function useCacheScopedToUser(client: QueryClient, userId: string | null): void {
  const previous = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (previous.current !== undefined && previous.current !== userId) client.clear();
    previous.current = userId;
  }, [client, userId]);
}
